import React, {useEffect} from 'react';
import { Box, Typography, Container, Button, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import ComputerIcon from '@mui/icons-material/Computer';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import MentorIcon from '@mui/icons-material/SupervisorAccount';

import {
    serviceTelehealth,
    serviceFamilyTherapy,
    serviceEarlyIntervention
} from '../../assets/images';
import useParallaxEffect from "../common/useParallaxEffect";
import ScrollToTop from "../common/ScrollToTop";
import { Service } from '../../types/types.ts';

const Services = () => {
    useParallaxEffect();

    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    
    const theme = useTheme();

    const services: Service[] = [
        {
            title: "In-Home ABA Therapy",
            description: "We provide personalized ABA therapy in the comfort of your home, promoting skill development in a familiar and natural environment. This allows our team to work with families to ensure consistency across the implemented interventions. Using this service model allows our team to focus on communication, daily living skills, behaviors that may only occur in the home setting, and social or play skills with siblings and parents.",
            icon: <HomeIcon />
        },
        {
            title: "In-Center ABA Therapy (Coming Soon!)",
            description: "A structured, engaging setting where individuals receive focused, one-on-one and group therapy with our expert staff. This setting will help our team prioritize language development, social skills groups, and pre-academic skills for school readiness. We can also assist families and their child transition into the school environment to ensure appropriate supports are in place to help them succeed in the new setting.",
            icon: <LocalHospitalIcon />
        },
        {
            title: "In-School ABA Support",
            description: "Our team can collaborate with educators to provide in-school or daycare behavioral interventions, social skills training, and individualized support for academic success. Our clinicians will work with parents to assist them with reviewing IEP goals and collaborate effectively during IEP meetings to ensure consistent implementation and appropriate supports are being put in place to foster success in the classroom setting. If approved by the family’s insurance and school, our techs can also provide support in the classroom as needed.",
            icon: <SchoolIcon />
        },
        {
            title: "Virtual ABA Therapy",
            description: "Pivotal provides flexible, remote ABA therapy sessions, consultations, or parent training to ensure continuous progress from anywhere.",
            icon: <ComputerIcon />
        },
        {
            title: "Parent Engagement Model ",
            description: "At Pivotal, we prioritize an individualized treatment approach for each family. We recognize that full-time ABA therapy may not be appropriate for all children. This model allows our team to fully support families who are looking for guidance on how to boost social skills, increase independence with daily living skills, and decrease maladaptive behaviors in the home or community.",
            icon: <FamilyRestroomIcon />
        },
        {
            title: "Mentorship",
            description: "Pivotal encourages continuous learning and innovation in the field. As a result, we support students who are actively in an ABA Master’s program by providing mentorship and guidance along with fieldwork hours. Feel free to reach out for more information!",
            icon: <MentorIcon />
        }
    ];

    return (
        <>
            <div id="back-to-top-anchor" />
            <Box sx={{ bgcolor: theme.palette.background.default, py: 8 }}>
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2
                            }}>
                                <Box
                                    component="img"
                                    src={serviceTelehealth}
                                    alt="Service Image"
                                    sx={{
                                        width: { xs: '100%', sm: '50%' },
                                        borderRadius: 7,
                                        height: { xs: '200px', sm: 'auto' },
                                        objectFit: 'cover'
                                    }}
                                />
                                <Box
                                    component="img"
                                    src={serviceEarlyIntervention}
                                    alt="Service Image"
                                    sx={{
                                        width: { xs: '100%', sm: '50%' },
                                        borderRadius: 7,
                                        height: { xs: '200px', sm: 'auto' },
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box
                                    component="img"
                                    src={serviceFamilyTherapy}
                                    alt="Service Image"
                                    sx={{
                                        width: '100%',
                                        borderRadius: 7,
                                        height: { xs: '200px', sm: 'auto' },
                                        objectFit: 'cover'
                                    }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5 }}
                            >
                                <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', mb: 3, color: 'text.primary' }}>
                                    Services Offered
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                                    At Pivotal Behavioral Services, we offer comprehensive ABA therapy tailored to meet the unique needs of individuals with Autism Spectrum Disorder. Our evidence-based approach fosters growth, independence, and meaningful progress in various settings.
                                </Typography>
                                {services.map((service, index) => (
                                    <Box key={index} sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Box sx={{ mr: 2, color: 'primary.main' }}>
                                                {service.icon}
                                            </Box>
                                            <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                                                {service.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {service.description}
                                        </Typography>
                                    </Box>
                                ))}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="large"
                                    component={RouterLink}
                                    to="/contact"
                                    sx={{
                                        mt: 4,
                                        px: 3,
                                        py: 1,
                                        borderRadius: 1,
                                        textTransform: 'none',
                                        fontSize: '1rem'
                                    }}
                                >
                                    Book Free Consultation
                                </Button>
                            </motion.div>
                        </Box>
                    </Box>
                </Container>
            </Box>
            <ScrollToTop />
        </>
    );
};

export default Services;